import { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import { getHistory } from "../services/api";
import { useEventLogger } from '../hooks/useEventLogger';

export default function History({ user, onPageChange }) {
  const { logNavigation, logLogout } = useEventLogger(user);
  const [historyData, setHistoryData] = useState({
    items: [],
    pagination: {},
    total_duration: 0,
    overall_duration: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const data = await getHistory(currentPage);
        setHistoryData(data);
      } catch (error) {
        console.error("Failed to fetch history:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [currentPage]);

  const {
    items: completedTasks,
    pagination,
    total_duration,
    overall_duration,
  } = historyData;

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return <div className="app">Loading history...</div>;
  }

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="history"
        onPageChange={onPageChange}
        title={user?.name || "User"}
        subtitle=""
        onLogout={logLogout}
        onNavigation={logNavigation}
      />
      <main className="annotator">
        <header className="annotator-header">
          <div style={{ width: "100%" }}>
            <h1>Annotation History</h1>
            <p>View your completed and skipped annotations</p>
            {(total_duration > 0 ||
              pagination.total_items > 0 ||
              overall_duration > 0) && (
              <div className="stats-cards">
                <div className="stat-card">
                  <div className="stat-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z" />
                      <path d="M19 7h-4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                      <path d="M5 11V7a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">
                      {pagination.total_items || 0}
                    </div>
                    <div className="stat-label">Your Tasks</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">
                      {formatDuration(total_duration)}
                    </div>
                    <div className="stat-label">Your Duration</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">
                      {formatDuration(overall_duration)}
                    </div>
                    <div className="stat-label">Team Progress</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>

        <section className="history-list">
          {completedTasks.length === 0 ? (
            <div className="empty-state">
              <p>No completed or skipped annotations yet</p>
            </div>
          ) : (
            <>
              <div className="history-grid">
                {completedTasks.map((task) => (
                  <div key={task.id} className="history-card">
                    <div className="history-header">
                      <h3>{task.title}</h3>
                      <span className={`status ${task.status}`}>
                        {task.status === "completed"
                          ? "✓ Completed"
                          : "⏭ Skipped"}
                      </span>
                    </div>
                    {task.status === "completed" && <p>{task.transcript}</p>}
                    {task.status === "skipped" && (
                      <p className="skipped-note">Task was skipped</p>
                    )}
                    <div className="history-meta">
                      <span>Duration: {formatDuration(task.duration)}</span>
                      <span>
                        {task.status === "completed" ? "Completed" : "Skipped"}:{" "}
                        {task.completed_at
                          ? new Date(task.completed_at).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.has_previous}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.current_page} of {pagination.total_pages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={!pagination.has_next}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
